// Equipment on Site register — per-project plant/equipment tracker (LOLER,
// PUWER, operator cards). Syncs live into the Weekly Site Report 5.0 table.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Loader2, Save, Trash2, Upload, FileText, AlertTriangle, Wrench } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const fi = "w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none";
const fiStyle = { borderColor: "#E5E0DA" };
const Lbl = ({ children }) => <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>{children}</span>;
const FILTERS = [{ id: "on_site", label: "On site" }, { id: "off_hired", label: "Off hired" }, { id: "all", label: "All" }];

function dueTone(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T00:00:00");
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const days = Math.round((d - today) / 86400000);
    if (days < 0) return { label: `Overdue (${dateStr})`, bg: "#FBE3E1", fg: "#B3261E" };
    if (days <= 7) return { label: `Due ${dateStr}`, bg: "#FDECD2", fg: "#A9631A" };
    return { label: dateStr, bg: "#EFF3EC", fg: "#4C7A4B" };
}

function DueBadge({ dateStr, na }) {
    if (na || !dateStr) return <span className="text-[11px]" style={{ color: "#B7B2AA" }}>N/A</span>;
    const t = dueTone(dateStr);
    return <span className="px-1.5 py-0.5 rounded-md text-[10.5px] font-semibold" style={{ background: t.bg, color: t.fg }}>{t.label}</span>;
}

function StatusBadge({ status }) {
    const TONES = { "In use": ["#EAF1FB", "#2A5DB0"], "Standby": ["#F0ECE5", "#8B867E"], "Broken down": ["#FBE3E1", "#B3261E"], "Off hire": ["#F0ECE5", "#6F6A64"] };
    const [bg, fg] = TONES[status] || TONES["In use"];
    return <span className="px-1.5 py-0.5 rounded-md text-[10.5px] font-semibold whitespace-nowrap" style={{ background: bg, color: fg }}>{status || "—"}</span>;
}

export default function EquipmentOnSitePanel({ clientId, projectId, canEdit, registerEnabled }) {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("on_site");
    const [meta, setMeta] = useState({ categories: [], owner_options: [], status_options: [] });
    const [loading, setLoading] = useState(true);
    const [dialogItem, setDialogItem] = useState(null); // {} = new, null = closed
    const [registerUploading, setRegisterUploading] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/equipment", { params: { project_id: projectId, status_filter: filter } })
            .then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
    };
    useEffect(() => { api.get("/equipment/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
    useEffect(load, [projectId, filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const patchItem = async (id, body) => {
        try {
            await api.patch(`/equipment/${id}`, body);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };
    const deleteItem = async (id) => {
        if (!window.confirm("Remove this item from the register?")) return;
        try { await api.delete(`/equipment/${id}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };
    const uploadRegisterFile = async (registerType, file) => {
        if (!file) return;
        setRegisterUploading(registerType);
        const fd = new FormData(); fd.append("file", file);
        try {
            await api.post("/equipment/register-upload", fd, {
                params: { project_id: projectId, register_type: registerType },
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`${registerType} register uploaded to the Document Library`);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setRegisterUploading(""); }
    };

    if (!registerEnabled) {
        return (
            <div className="rounded-xl border p-6 text-center" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }} data-testid="equipment-not-enabled">
                <Wrench className="w-6 h-6 mx-auto mb-2" style={{ color: "#B7B2AA" }} />
                <p className="text-[13px] font-semibold text-[#1F1F1F]">Equipment on Site register is not enabled</p>
                <p className="text-[12px] mt-1" style={{ color: "#8B867E" }}>Turn it on in Client settings to track plant, LOLER / PUWER checks and feed the Weekly Site Report 5.0 section.</p>
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="equipment-on-site-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex gap-1.5">
                    {FILTERS.map((f) => (
                        <button key={f.id} data-testid={`equipment-filter-${f.id}`} onClick={() => setFilter(f.id)}
                            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border ${filter === f.id ? "text-white border-transparent" : "bg-white text-[#2D2D2D]"}`}
                            style={filter === f.id ? { background: "var(--brand-accent,#C75A1B)" } : fiStyle}>{f.label}</button>
                    ))}
                </div>
                {canEdit && (
                    <div className="flex gap-2 items-center">
                        {["LOLER", "PUWER"].map((rt) => (
                            <label key={rt} className="text-[11.5px] font-semibold px-3 py-1.5 rounded-lg border bg-white cursor-pointer inline-flex items-center gap-1.5"
                                   style={fiStyle} data-testid={`equipment-register-upload-${rt.toLowerCase()}`}>
                                {registerUploading === rt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                Upload {rt} register
                                <input type="file" className="hidden" onChange={(e) => uploadRegisterFile(rt, e.target.files?.[0])} />
                            </label>
                        ))}
                        <button data-testid="equipment-add-btn" onClick={() => setDialogItem({})}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            <Plus className="w-3.5 h-3.5" /> Add equipment
                        </button>
                    </div>
                )}
            </div>

            <div className="rounded-xl border overflow-hidden bg-white" style={{ borderColor: "#E5E0DA" }}>
                <div className="overflow-x-auto">
                    <table className="w-full text-[12.5px]">
                        <thead>
                            <tr className="text-left" style={{ background: "#F5F3F0" }}>
                                {["Item", "Category", "Plant no.", "Owner / hire", "Status", "LOLER", "PUWER", "Operator card", ""].map((h) => (
                                    <th key={h} className="px-3 py-2 text-[10.5px] font-semibold uppercase tracking-[.04em]" style={{ color: "#8B867E" }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={9} className="px-3 py-4 italic text-center" style={{ color: "#8B867E" }}>Loading…</td></tr>}
                            {!loading && items.length === 0 && <tr><td colSpan={9} className="px-3 py-4 italic text-center" style={{ color: "#8B867E" }}>No equipment recorded yet.</td></tr>}
                            {!loading && items.map((it) => (
                                <tr key={it.id} data-testid={`equipment-row-${it.id}`} className="border-t" style={{ borderColor: "#F0ECE5" }}>
                                    <td className="px-3 py-2 font-semibold text-[#1F1F1F] cursor-pointer" onClick={() => canEdit && setDialogItem(it)}>{it.item}</td>
                                    <td className="px-3 py-2" style={{ color: "#6F6A64" }}>{it.category}</td>
                                    <td className="px-3 py-2" style={{ color: "#6F6A64" }}>{it.plant_no || "—"}</td>
                                    <td className="px-3 py-2" style={{ color: "#6F6A64" }}>{it.owner === "Hire" ? `Hire${it.hire_company ? ` - ${it.hire_company}` : ""}` : "Direct"}</td>
                                    <td className="px-3 py-2"><StatusBadge status={it.status} /></td>
                                    <td className="px-3 py-2"><DueBadge dateStr={it.loler_next_due} na={!it.loler_applies} /></td>
                                    <td className="px-3 py-2"><DueBadge dateStr={it.puwer_next_due} /></td>
                                    <td className="px-3 py-2"><DueBadge dateStr={it.operator_card_expiry} /></td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        {canEdit && (
                                            <div className="inline-flex gap-2">
                                                {it.status !== "Off hire" && (
                                                    <button data-testid={`equipment-offhire-${it.id}`} title="Mark off hire"
                                                        onClick={() => patchItem(it.id, { status: "Off hire", off_hire_date: new Date().toISOString().slice(0, 10) })}
                                                        className="text-[11px] font-semibold hover:underline" style={{ color: "#6F6A64" }}>Off hire</button>
                                                )}
                                                {it.status !== "Broken down" && (
                                                    <button data-testid={`equipment-broken-${it.id}`} title="Mark broken down"
                                                        onClick={() => patchItem(it.id, { status: "Broken down" })}
                                                        className="text-[11px] font-semibold hover:underline" style={{ color: "#B3261E" }}>Broken down</button>
                                                )}
                                                <button data-testid={`equipment-delete-${it.id}`} onClick={() => deleteItem(it.id)}
                                                    className="hover:text-[#8A2A21]" style={{ color: "#B33A2E" }}><Trash2 className="w-3.5 h-3.5" /></button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {dialogItem && (
                <EquipmentDialog clientId={clientId} projectId={projectId} item={dialogItem} meta={meta}
                    onClose={() => setDialogItem(null)} onSaved={(updated) => { load(); if (updated) setDialogItem(updated); }} />
            )}
        </div>
    );
}

function EquipmentDialog({ clientId, projectId, item, meta, onClose, onSaved }) {
    const isNew = !item.id;
    const [f, setF] = useState({
        item: item.item || "", category: item.category || "Other", plant_no: item.plant_no || "",
        owner: item.owner || "Direct", hire_company: item.hire_company || "",
        on_hire_date: item.on_hire_date || "", off_hire_date: item.off_hire_date || "",
        status: item.status || "In use", loler_applies: !!item.loler_applies,
        loler_last_exam: item.loler_last_exam || "", loler_next_due: item.loler_next_due || "",
        puwer_last_inspected: item.puwer_last_inspected || "", puwer_inspected_by: item.puwer_inspected_by || "",
        puwer_next_due: item.puwer_next_due || "", operator_card_type: item.operator_card_type || "",
        operator_card_expiry: item.operator_card_expiry || "", notes: item.notes || "",
    });
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState("");
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

    async function submit() {
        if (!f.item.trim()) { toast.error("Item name is required"); return; }
        setBusy(true);
        try {
            if (isNew) {
                const { data } = await api.post("/equipment", { client_id: clientId, project_id: projectId, ...f });
                toast.success("Added to the register");
                onSaved(data);
            } else {
                await api.patch(`/equipment/${item.id}`, f);
                toast.success("Saved");
                onSaved(null);
                onClose();
            }
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    }

    async function viewFile(field) {
        try {
            const resp = await api.get(`/equipment/${item.id}/download/${field}`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: resp.headers["content-type"] }));
            window.open(url, "_blank");
        } catch { toast.error("Could not open file"); }
    }

    async function uploadFile(field, endpoint, file) {
        if (!file || !item.id) return;
        setUploading(field);
        const fd = new FormData(); fd.append("file", file);
        try {
            const { data } = await api.post(`/equipment/${item.id}/${endpoint}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("File uploaded");
            onSaved(data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setUploading(""); }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="equipment-dialog" className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[640px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 pt-4 pb-3 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="font-display text-[16px] font-bold text-[#1F1F1F]">{isNew ? "Add equipment" : "Edit equipment"}</h3>
                    <button data-testid="equipment-dialog-close" onClick={onClose} className="w-8 h-8 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-5 py-4 overflow-y-auto space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block"><Lbl>Item</Lbl><input data-testid="equipment-field-item" value={f.item} onChange={(e) => set("item", e.target.value)} className={fi} style={fiStyle} placeholder="e.g. 21T Excavator" /></label>
                        <label className="block"><Lbl>Category</Lbl><select data-testid="equipment-field-category" value={f.category} onChange={(e) => set("category", e.target.value)} className={fi} style={fiStyle}>{(meta.categories || []).map((c) => <option key={c}>{c}</option>)}</select></label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block"><Lbl>Plant / serial no.</Lbl><input data-testid="equipment-field-plantno" value={f.plant_no} onChange={(e) => set("plant_no", e.target.value)} className={fi} style={fiStyle} /></label>
                        <label className="block"><Lbl>Status</Lbl><select data-testid="equipment-field-status" value={f.status} onChange={(e) => set("status", e.target.value)} className={fi} style={fiStyle}>{(meta.status_options || []).map((s) => <option key={s}>{s}</option>)}</select></label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block"><Lbl>Owner</Lbl><select data-testid="equipment-field-owner" value={f.owner} onChange={(e) => set("owner", e.target.value)} className={fi} style={fiStyle}>{(meta.owner_options || []).map((o) => <option key={o}>{o}</option>)}</select></label>
                        {f.owner === "Hire" && <label className="block"><Lbl>Hire company</Lbl><input data-testid="equipment-field-hirecompany" value={f.hire_company} onChange={(e) => set("hire_company", e.target.value)} className={fi} style={fiStyle} /></label>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block"><Lbl>On hire date</Lbl><input data-testid="equipment-field-onhire" type="date" value={f.on_hire_date} onChange={(e) => set("on_hire_date", e.target.value)} className={fi} style={fiStyle} /></label>
                        <label className="block"><Lbl>Off hire date</Lbl><input data-testid="equipment-field-offhire" type="date" value={f.off_hire_date} onChange={(e) => set("off_hire_date", e.target.value)} className={fi} style={fiStyle} /></label>
                    </div>

                    <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                        <label className="flex items-center gap-2 text-[12px] font-semibold" style={{ color: "#1F1F1F" }}>
                            <input data-testid="equipment-field-loler-applies" type="checkbox" checked={f.loler_applies} onChange={(e) => set("loler_applies", e.target.checked)} /> LOLER applies to this item
                        </label>
                        {f.loler_applies && (
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block"><Lbl>Last exam</Lbl><input data-testid="equipment-field-loler-last" type="date" value={f.loler_last_exam} onChange={(e) => set("loler_last_exam", e.target.value)} className={fi} style={fiStyle} /></label>
                                <label className="block"><Lbl>Next due</Lbl><input data-testid="equipment-field-loler-next" type="date" value={f.loler_next_due} onChange={(e) => set("loler_next_due", e.target.value)} className={fi} style={fiStyle} /></label>
                            </div>
                        )}
                        {!isNew && f.loler_applies && (
                            <div className="flex items-center gap-2 text-[11.5px]">
                                {item.loler_certificate?.filename && (
                                    <button type="button" onClick={() => viewFile("loler_certificate")}
                                       className="inline-flex items-center gap-1 hover:underline" style={{ color: "#2A5DB0" }}><FileText className="w-3.5 h-3.5" />{item.loler_certificate.filename}</button>
                                )}
                                <label className="font-semibold cursor-pointer inline-flex items-center gap-1" style={{ color: "#C75A1B" }} data-testid="equipment-upload-loler">
                                    {uploading === "loler_certificate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload certificate
                                    <input type="file" className="hidden" onChange={(e) => uploadFile("loler_certificate", "upload-loler", e.target.files?.[0])} />
                                </label>
                            </div>
                        )}
                    </div>

                    <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                        <Lbl>PUWER</Lbl>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block"><Lbl>Last inspected</Lbl><input data-testid="equipment-field-puwer-last" type="date" value={f.puwer_last_inspected} onChange={(e) => set("puwer_last_inspected", e.target.value)} className={fi} style={fiStyle} /></label>
                            <label className="block"><Lbl>Next due</Lbl><input data-testid="equipment-field-puwer-next" type="date" value={f.puwer_next_due} onChange={(e) => set("puwer_next_due", e.target.value)} className={fi} style={fiStyle} /></label>
                        </div>
                        <label className="block"><Lbl>Inspected by</Lbl><input data-testid="equipment-field-puwer-by" value={f.puwer_inspected_by} onChange={(e) => set("puwer_inspected_by", e.target.value)} className={fi} style={fiStyle} /></label>
                        {!isNew && (
                            <div className="flex items-center gap-2 text-[11.5px]">
                                {item.puwer_record?.filename && (
                                    <button type="button" onClick={() => viewFile("puwer_record")}
                                       className="inline-flex items-center gap-1 hover:underline" style={{ color: "#2A5DB0" }}><FileText className="w-3.5 h-3.5" />{item.puwer_record.filename}</button>
                                )}
                                <label className="font-semibold cursor-pointer inline-flex items-center gap-1" style={{ color: "#C75A1B" }} data-testid="equipment-upload-puwer">
                                    {uploading === "puwer_record" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload record
                                    <input type="file" className="hidden" onChange={(e) => uploadFile("puwer_record", "upload-puwer", e.target.files?.[0])} />
                                </label>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <label className="block"><Lbl>Operator card type</Lbl><input data-testid="equipment-field-cardtype" value={f.operator_card_type} onChange={(e) => set("operator_card_type", e.target.value)} className={fi} style={fiStyle} placeholder="e.g. CPCS / NPORS" /></label>
                        <label className="block"><Lbl>Operator card expiry</Lbl><input data-testid="equipment-field-cardexpiry" type="date" value={f.operator_card_expiry} onChange={(e) => set("operator_card_expiry", e.target.value)} className={fi} style={fiStyle} /></label>
                    </div>
                    <label className="block"><Lbl>Notes (action taken — repairs / replacement / re-planning)</Lbl><textarea data-testid="equipment-field-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} className={fi + " min-h-[64px]"} style={fiStyle} /></label>

                    {isNew && (
                        <p className="text-[11px] flex items-center gap-1.5" style={{ color: "#8B867E" }}>
                            <AlertTriangle className="w-3.5 h-3.5" /> Save first, then you can attach the LOLER certificate / PUWER record.
                        </p>
                    )}
                </div>
                <div className="flex items-center justify-end gap-2.5 px-5 py-3 border-t" style={{ borderColor: "#F0EDE8" }}>
                    <button data-testid="equipment-dialog-submit" onClick={submit} disabled={busy}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {isNew ? "Add" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
